import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Stack,
  IconButton,
  InputAdornment,
  Slider,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Lock,
  Send,
  ContentCopy,
  Security,
  CheckCircle,
  AutorenewOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { GlassCard } from '../components/ui/GlassCard';
import UserSelector from '../components/UserSelector';
import { AgentUser } from '../types';

const SecureMessages: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  const [sending, setSending] = useState(false);
  const [showCode, setShowCode] = useState(true);

  const [secureMessage, setSecureMessage] = useState({
    recipient: null as AgentUser | null,
    code: generateRandomCode(),
    message: '',
    codeTimeout: 5, // minutes
    displayTimeout: 60, // seconds
  });

  function generateRandomCode() {
    return Math.floor(1000 + Math.random() * 9000).toString();
  }

  const handleSendSecureMessage = async () => {
    if (!secureMessage.recipient || !secureMessage.message) return;

    setSending(true);
    try {
      const socket = socketService.getSocket();
      if (!socket) {
        console.error('Socket not connected');
        setSending(false);
        return;
      }

      socket.emit('sendSecureMessage', {
        recipientId: secureMessage.recipient.agentUserId || secureMessage.recipient.userId,
        message: secureMessage.message,
        code: secureMessage.code,
        codeTimeout: secureMessage.codeTimeout,
        displayTimeout: secureMessage.displayTimeout,
      }, (response: any) => {
        if (response.success) {
          console.log('Secure message sent:', response.messageId);
          // Reset form
          setSecureMessage({
            ...secureMessage,
            message: '',
            code: generateRandomCode(),
          });
        } else {
          console.error('Failed to send secure message:', response.error);
        }
        setSending(false);
      });
    } catch (error) {
      console.error('Failed to send secure message:', error);
      setSending(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    // Listen for incoming secure messages
    socket.on('secureMessage', (message: any) => {
      console.log('Received secure message:', message);
      // Handle incoming secure message notification
    });

    socket.on('secureMessageRead', (data: any) => {
      console.log('Secure message read:', data);
      // Handle message read notification
    });

    return () => {
      socket.off('secureMessage');
      socket.off('secureMessageRead');
    };
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Secure Messages"
        subtitle="Send encrypted messages protected by security codes"
        breadcrumbs={[
          { label: 'Home', href: '/dashboard' },
          { label: 'Secure Messages' },
        ]}
      />

      <Stack spacing={3}>
        <Alert severity="info" icon={<Security />}>
          Send a message protected by a security code. Provide the code to the recipient over the phone
          to ensure only they can view the message.
        </Alert>

        <GlassCard>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            Recipient & Security
          </Typography>

          <Stack spacing={3}>
            <UserSelector
              value={secureMessage.recipient}
              onChange={(user) => setSecureMessage({ ...secureMessage, recipient: user })}
              label="Select Recipient"
              required
              showCompanyFilter
            />

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
              <TextField
                label="Security Code"
                value={secureMessage.code}
                onChange={(e) => setSecureMessage({ ...secureMessage, code: e.target.value })}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCode(!showCode)}
                        edge="end"
                      >
                        {showCode ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                      <IconButton
                        onClick={() => copyToClipboard(secureMessage.code)}
                        edge="end"
                      >
                        <ContentCopy />
                      </IconButton>
                      <IconButton
                        onClick={() => setSecureMessage({ ...secureMessage, code: generateRandomCode() })}
                        edge="end"
                      >
                        <AutorenewOutlined />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                type={showCode ? 'text' : 'password'}
                helperText="Provide this code to the recipient verbally"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Code Entry Timeout
                </Typography>
                <Stack spacing={2}>
                  <Slider
                    value={secureMessage.codeTimeout}
                    onChange={(e, v) => setSecureMessage({ ...secureMessage, codeTimeout: v as number })}
                    min={1}
                    max={30}
                    step={1}
                    marks={[
                      { value: 5, label: '5 min' },
                      { value: 15, label: '15 min' },
                      { value: 30, label: '30 min' },
                    ]}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 4 }}>
                    User has {secureMessage.codeTimeout} minutes to enter the code
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Message Display Timeout
                </Typography>
                <Stack spacing={2}>
                  <Slider
                    value={secureMessage.displayTimeout}
                    onChange={(e, v) => setSecureMessage({ ...secureMessage, displayTimeout: v as number })}
                    min={10}
                    max={300}
                    step={10}
                    marks={[
                      { value: 30, label: '30s' },
                      { value: 60, label: '1m' },
                      { value: 300, label: '5m' },
                    ]}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 4 }}>
                    Message visible for {secureMessage.displayTimeout} seconds after code entry
                  </Typography>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </GlassCard>

        <GlassCard>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
            Secure Message
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Message"
            placeholder="Enter your secure message here..."
            value={secureMessage.message}
            onChange={(e) => setSecureMessage({ ...secureMessage, message: e.target.value })}
            required
            helperText={`${secureMessage.message.length} characters`}
          />
        </GlassCard>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => setSecureMessage({
              recipient: null,
              code: generateRandomCode(),
              message: '',
              codeTimeout: 5,
              displayTimeout: 60,
            })}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <Send />}
            onClick={handleSendSecureMessage}
            disabled={!secureMessage.recipient || !secureMessage.message || sending}
            sx={{
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                : theme.palette.primary.main,
            }}
          >
            Send Secure Message
          </Button>
        </Box>

        {/* Code Display Card */}
        {secureMessage.recipient && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Alert severity="success" icon={<CheckCircle />}>
              <Typography variant="subtitle2" fontWeight={600}>
                Ready to Send
              </Typography>
              <Typography variant="body2">
                Provide this code to {secureMessage.recipient.fullName} over the phone:
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 1, fontFamily: 'monospace' }}>
                {secureMessage.code}
              </Typography>
            </Alert>
          </motion.div>
        )}
      </Stack>
    </Container>
  );
};

export default SecureMessages;